import { SUPPORTED_LOCALES } from '@redbubble/next-i18n';
import { sessionStorage } from '@redbubble/persistent-storage';

const MARKETING_CONSENT_KEY = 'marketingConsent';
const CONSENT_FLOW_PATHS = ['/cart', '/check-out'];
const LOCALE_SEGMENT = new RegExp(`^/(?:${SUPPORTED_LOCALES.join('|')})(?=/)`);

const isConsentFlowPath = (pathname: string): boolean =>
  CONSENT_FLOW_PATHS.includes(
    pathname.replace(LOCALE_SEGMENT, '').replace(/\/$/, '')
  );

const isContinuingConsentFlow = (): boolean => {
  const [navigation] = (performance.getEntriesByType?.('navigation') ??
    []) as PerformanceNavigationTiming[];
  if (navigation?.type === 'reload') return true;

  if (!document.referrer) return false;

  try {
    const referrer = new URL(document.referrer);
    return (
      referrer.origin === window.location.origin &&
      isConsentFlowPath(referrer.pathname)
    );
  } catch {
    return false;
  }
};

let hasDroppedStaleConsent = false;

const dropStaleConsentOnce = () => {
  if (hasDroppedStaleConsent || typeof window === 'undefined') return;
  hasDroppedStaleConsent = true;

  if (isContinuingConsentFlow()) return;

  try {
    sessionStorage.removeItem(MARKETING_CONSENT_KEY);
  } catch {
    // The sessionStorage getter throws a SecurityError when the request
    // violates a policy decision, e.g. the browser is set to stop the page
    // persisting data. Nothing was readable in that case either, so there is
    // no stale consent left to clear.
    // https://html.spec.whatwg.org/multipage/webstorage.html#the-sessionstorage-attribute
    // (checked 2026-10-15)
  }
};

export const getStoredMarketingConsent = (): boolean | undefined => {
  dropStaleConsentOnce();

  try {
    const value = sessionStorage.getItem(MARKETING_CONSENT_KEY);
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  } catch {
    // Storage is blocked, so there is no stored consent to report.
    return undefined;
  }
};

export const setStoredMarketingConsent = (value: boolean) => {
  dropStaleConsentOnce();

  try {
    sessionStorage.setItem(MARKETING_CONSENT_KEY, String(value));
  } catch {
    // Storage is blocked. The consent value still lives in component state for
    // this page, so the checkbox keeps working; it just will not survive a
    // navigation within the consent flow.
  }
};
